import { useState } from "react";
import { Shield, CheckCircle, Copy, AlertOctagon, CalendarClock } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { cn } from "@/lib/utils";
import { format, formatDistanceToNow } from "date-fns";
import { useToast } from "@/hooks/use-toast";
import { SoundSelector } from "@/components/sound-selector";
import { PulseReactionsInline } from "@/components/pulse-reactions";
import { ClientUploadNotificationDetails, ClientUploadReviewBadge } from "@/components/client-upload-notification-details";

interface AdminNotification {
  id: number;
  userId: number;
  message: string;
  type: string;
  isRead: boolean;
  readAt: string | null;
  metadata: Record<string, any>;
  createdAt: string;
  createdBy: number | null;
}

interface ShareEmailRecipient {
  email: string;
  name?: string;
  type: "to" | "cc";
}

function safePreviewHref(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const candidate = value.trim();
  // Only the app's relative preview route is allowed. In particular, reject
  // protocol-relative URLs (//host) and any javascript/data URL before it can
  // become an href.
  if (!/^\/p\/[^/]+(?:[/?#].*)?$/.test(candidate)) return null;
  try {
    const parsed = new URL(candidate, "http://localhost");
    if (parsed.origin !== "http://localhost" || !parsed.pathname.startsWith("/p/")) return null;
    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return null;
  }
}

function visibleRecipients(value: unknown): ShareEmailRecipient[] {
  if (Array.isArray(value)) {
    return value.filter((recipient): recipient is ShareEmailRecipient => {
      if (!recipient || typeof recipient !== "object") return false;
      const candidate = recipient as { type?: unknown; email?: unknown };
      return (candidate.type === "to" || candidate.type === "cc")
        && typeof candidate.email === "string"
        && candidate.email.trim().length > 0;
    });
  }
  if (!value || typeof value !== "object") return [];
  const grouped = value as { to?: unknown; cc?: unknown };
  return (["to", "cc"] as const).flatMap((type) => {
    const entries = Array.isArray(grouped[type]) ? grouped[type] : [];
    return entries.flatMap((entry) => {
      if (typeof entry === "string" && entry.trim()) return [{ email: entry.trim(), type }];
      if (entry && typeof entry === "object" && typeof (entry as ShareEmailRecipient).email === "string") {
        return [{ email: (entry as ShareEmailRecipient).email.trim(), name: (entry as ShareEmailRecipient).name, type }];
      }
      return [];
    });
  });
}

function ShareEmailWaitingDetails({ metadata }: { metadata: Record<string, any> }) {
  const sender = metadata.sender && typeof metadata.sender === "object" ? metadata.sender : {};
  const senderName = typeof metadata.senderName === "string"
    ? metadata.senderName
    : typeof sender.name === "string" ? sender.name : "Unknown sender";
  const senderUserId = metadata.senderUserId ?? sender.id;
  const href = safePreviewHref(metadata.previewUrl);
  const recipients = visibleRecipients(metadata.recipients);
  const files = Array.isArray(metadata.files) ? metadata.files : [];
  const toRecipients = recipients.filter((recipient) => recipient.type === "to");
  const ccRecipients = recipients.filter((recipient) => recipient.type === "cc");

  return (
    <div
      className="mt-2 rounded-md border border-amber-700/70 bg-amber-950/30 p-2 text-xs text-gray-200"
      data-testid="share-email-waiting-details"
    >
      <div className="grid grid-cols-[auto_1fr] gap-x-2 gap-y-1">
        <span className="text-gray-400">Status</span><span>Waiting for delivery</span>
        <span className="text-gray-400">Sender</span><span>{senderName}{senderUserId != null ? ` (user ${String(senderUserId)})` : ""}</span>
        {metadata.deliveryId && <><span className="text-gray-400">Delivery</span><span className="break-all">{String(metadata.deliveryId)}</span></>}
        {metadata.projectName && <><span className="text-gray-400">Project</span><span>{String(metadata.projectName)}{metadata.projectId ? ` (${String(metadata.projectId)})` : ""}</span></>}
        {metadata.previewTitle && <><span className="text-gray-400">Preview</span><span>{String(metadata.previewTitle)}{metadata.previewShareId ? ` (#${String(metadata.previewShareId)})` : ""}</span></>}
        {metadata.subject && <><span className="text-gray-400">Subject</span><span className="break-words">{String(metadata.subject)}</span></>}
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-2">
        <span className="text-gray-400">Preview link</span>
        {href ? (
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="break-all text-blue-300 underline hover:text-blue-200"
            onClick={(event) => event.stopPropagation()}
            onPointerDown={(event) => event.stopPropagation()}
            onKeyDown={(event) => event.stopPropagation()}
          >
            Open preview
          </a>
        ) : (
          <span className="text-gray-500">Unavailable</span>
        )}
      </div>

      {(toRecipients.length > 0 || ccRecipients.length > 0) && (
        <div className="mt-2 space-y-1">
          {toRecipients.length > 0 && <p><span className="text-gray-400">To:</span> {toRecipients.map((recipient) => recipient.email).join(", ")}</p>}
          {ccRecipients.length > 0 && <p><span className="text-gray-400">Cc:</span> {ccRecipients.map((recipient) => recipient.email).join(", ")}</p>}
        </div>
      )}

      {files.length > 0 && (
        <div className="mt-2">
          <p className="text-gray-400">Files</p>
          <ul className="mt-1 space-y-1">
            {files.map((file, index) => (
              <li key={file?.id ?? `${file?.fileName ?? "file"}-${index}`} className="flex items-start justify-between gap-2">
                <span className="min-w-0 break-words">{String(file?.fileName ?? "Unnamed file")}{file?.detail ? ` — ${String(file.detail)}` : ""}</span>
                <span className={file?.ready === true ? "shrink-0 text-emerald-300" : "shrink-0 text-amber-300"}>
                  {file?.ready === true ? "Ready" : "Waiting"}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function isCalendarNotification(notification: AdminNotification): boolean {
  return notification.type.startsWith('calendar_')
    || notification.metadata?.calendar === true
    || notification.metadata?.action === 'project_timing_changed'
    || !!notification.metadata?.calendarEventId
    || !!notification.metadata?.calendar_event_id;
}

export function AdminNotificationButton() {
  const { toast } = useToast();
  const [isOpen, setIsOpen] = useState(false);

  // Lightweight count query for badge (always enabled for instant updates)
  const { data: countData } = useQuery<{ count: number; redCount?: number; amberCount?: number }>({
    queryKey: ['/api/admin-notifications/count'],
    staleTime: 30000, // 30 seconds - stays fresh without excessive refetches
    refetchInterval: 60000, // Refetch every minute as fallback
  });

  const unreadCount = countData?.count || 0;

  // Fetch full admin notifications list (only when dropdown is open)
  const { data: adminNotifications = [], isLoading: isLoadingNotifications, refetch: refetchNotifications } = useQuery<AdminNotification[]>({
    queryKey: ['/api/admin-notifications', { unreadOnly: true }],
    enabled: isOpen, // Only fetch when dropdown is open
  });

  // Mark as read mutation
  const markReadMutation = useMutation({
    mutationFn: async (notificationId: number) => {
      const response = await apiRequest('PATCH', `/api/admin-notifications/${notificationId}/read`, {});
      if (!response.ok) {
        throw new Error('Failed to mark notification as read');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/admin-notifications'] });
      queryClient.invalidateQueries({ queryKey: ['/api/admin-notifications/count'] });
      toast({
        title: "Success",
        description: "Notification marked as read",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to mark notification as read",
        variant: "destructive",
      });
    },
  });

  const handleMarkAsRead = (e: React.MouseEvent, notificationId: number) => {
    e.stopPropagation();
    markReadMutation.mutate(notificationId);
  };

  const handleCopyError = async (e: React.MouseEvent, notification: AdminNotification) => {
    e.stopPropagation();
    const text = notification.metadata?.copyText
      || [notification.message, notification.metadata?.errorCode ? `Code: ${notification.metadata.errorCode}` : null, notification.metadata?.errorMessage ? `Message: ${notification.metadata.errorMessage}` : null].filter(Boolean).join('\n');
    try {
      await navigator.clipboard.writeText(text);
      toast({ title: "Copied", description: "Error details copied to clipboard" });
    } catch {
      toast({ title: "Copy failed", description: "Could not access clipboard", variant: "destructive" });
    }
  };

  const handleNotificationClick = (notification: AdminNotification) => {
    const pulseId = notification.metadata?.pulseId;
    const calendarEventId =
      notification.metadata?.calendarEventId
      ?? notification.metadata?.calendar_event_id
      ?? (notification.type.startsWith('calendar_') ? notification.metadata?.eventId : undefined);
    if (pulseId) {
      window.dispatchEvent(new CustomEvent('open-pulse-workbench', {
        detail: { pulseId }
      }));
      markReadMutation.mutate(notification.id);
      setIsOpen(false);
    } else if (calendarEventId) {
      window.dispatchEvent(new CustomEvent('open-calendar-event', {
        detail: { eventId: String(calendarEventId) },
      }));
      markReadMutation.mutate(notification.id);
      setIsOpen(false);
    } else if (isCalendarNotification(notification)) {
      window.dispatchEvent(new CustomEvent('open-calendar'));
      markReadMutation.mutate(notification.id);
      setIsOpen(false);
    }
  };

  const formatNotificationDate = (dateString: string): string => {
    const notificationDate = new Date(dateString);
    const now = new Date();
    const hoursDiff = (now.getTime() - notificationDate.getTime()) / (1000 * 60 * 60);
    
    // Show relative time for notifications within the last 24 hours
    if (hoursDiff < 24) {
      return formatDistanceToNow(notificationDate, { addSuffix: true });
    }
    
    // Show absolute date for older notifications
    return format(notificationDate, 'MMM d, yyyy HH:mm');
  };

  // Filter and sort admin notifications
  const sortedNotifications = [...adminNotifications]
    .filter(notification => !notification.isRead)
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

  // Handle dropdown open/close with manual refetch as fallback
  const handleOpenChange = (newOpen: boolean) => {
    setIsOpen(newOpen);
    
    // When opening the dropdown, manually refetch to ensure fresh data
    if (newOpen) {
      refetchNotifications();
    }
  };

  return (
    <DropdownMenu open={isOpen} onOpenChange={handleOpenChange}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          className="relative navy-card border-gray-600 text-gray-300 hover:bg-gray-700"
          data-testid="button-admin-notification"
        >
          <Shield className="w-4 h-4" />
          {unreadCount > 0 && (
            <span 
              className={cn("absolute -top-1 -right-1 flex items-center justify-center w-5 h-5 text-xs font-bold rounded-full",
                (countData?.redCount ?? 0) > 0 ? "bg-red-600 text-white" :
                (countData?.amberCount ?? 0) === unreadCount ? "bg-amber-900 text-amber-200" : "bg-orange-500 text-white")}
              data-testid="badge-admin-count"
            >
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent 
        align="end" 
        className="w-[450px] bg-gray-800 border-gray-600 max-h-[780px] overflow-y-auto"
      >
        <DropdownMenuLabel className="flex items-center justify-between text-gray-300">
          <span className="flex items-center gap-2">
            <Shield className="w-4 h-4" />
            Admin Notifications ({sortedNotifications.length})
          </span>
          <SoundSelector soundType="soundAdminNotifications" label="Sound" />
        </DropdownMenuLabel>
        <DropdownMenuSeparator className="bg-gray-700" />
        
        {isLoadingNotifications ? (
          <div className="p-4 text-center text-gray-400">
            Loading admin notifications...
          </div>
        ) : sortedNotifications.length === 0 ? (
          <div className="p-4 text-center text-gray-400">
            No unread admin notifications
          </div>
        ) : (
          <div className="max-h-[680px] overflow-y-auto">
            {sortedNotifications.map((notification) => (
              <DropdownMenuItem
                key={notification.id}
                className={cn(
                  "flex flex-col gap-2 p-3",
                   notification.type === 'system_error' || (notification.type === 'client_upload_review' && notification.metadata?.severity === 'red')
                    ? "bg-red-500/10 hover:bg-red-500/20 border-l-2 border-red-500"
                     : notification.type === 'client_upload_review'
                     ? "bg-amber-950/20 hover:bg-amber-950/30 border-l-2 border-amber-800"
                    : "bg-orange-500/10 hover:bg-orange-500/20 border-l-2 border-orange-500",
                   (notification.metadata?.pulseId
                     || notification.metadata?.calendarEventId
                     || notification.metadata?.calendar_event_id
                     || isCalendarNotification(notification))
                     ? "cursor-pointer"
                     : "cursor-default"
                )}
                onSelect={(e) => {
                  e.preventDefault();
                   if (
                     notification.metadata?.pulseId
                     || notification.metadata?.calendarEventId
                     || notification.metadata?.calendar_event_id
                     || isCalendarNotification(notification)
                   ) {
                    handleNotificationClick(notification);
                  }
                }}
                data-testid={`admin-notification-${notification.id}`}
              >
                <div className="flex items-start justify-between gap-2 w-full">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      {notification.type === 'client_upload_review' ? <ClientUploadReviewBadge severity={notification.metadata?.severity} /> : <Badge className={cn(
                        "text-xs text-white border-0",
                        notification.type === 'system_error' ? "bg-red-600" : "bg-orange-500"
                      )}>
                        {notification.type === 'system_error' ? (
                          <span className="flex items-center gap-1"><AlertOctagon className="w-3 h-3" />system error</span>
                        ) : notification.type}
                      </Badge>}
                      {isCalendarNotification(notification) && (
                        <CalendarClock className="w-3.5 h-3.5 text-blue-300" aria-label="Calendar notification" />
                      )}
                      {notification.type === 'system_error' && notification.metadata?.system && (
                        <Badge variant="outline" className="text-xs text-gray-300 border-gray-500">
                          {notification.metadata.system}
                        </Badge>
                      )}
                    </div>
                    <p className={cn(
                      "text-sm text-gray-200",
                      notification.type === 'system_error' && "font-medium"
                    )}>
                      {notification.message}
                    </p>
                    {notification.type === 'share_email_waiting' && (
                      <ShareEmailWaitingDetails metadata={notification.metadata || {}} />
                    )}
                     {notification.type === 'client_upload_review' && (
                       <ClientUploadNotificationDetails metadata={notification.metadata || {}} />
                     )}
                    {notification.type === 'system_error' && notification.metadata?.errorMessage && (
                      <pre className="mt-1 text-xs text-red-300/90 bg-black/30 rounded p-2 whitespace-pre-wrap break-all max-h-32 overflow-y-auto font-mono">
                        {notification.metadata.errorCode ? `[${notification.metadata.errorCode}] ` : ''}{notification.metadata.errorMessage}
                      </pre>
                    )}
                    <div className="flex items-center justify-between mt-2">
                      <p className="text-xs text-gray-300 font-medium">
                        {formatNotificationDate(notification.createdAt)}
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center gap-2 w-full pt-2 border-t border-gray-700">
                  {(() => {
                    const pulseId = Number(notification.metadata?.pulseId);
                    if (!Number.isFinite(pulseId) || pulseId <= 0) return null;
                    return (
                      <div
                        onClick={(e) => e.stopPropagation()}
                        onPointerDown={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.stopPropagation()}
                      >
                        <PulseReactionsInline
                          pulseId={pulseId}
                          projectId={String(notification.metadata?.projectId ?? '')}
                        />
                      </div>
                    );
                  })()}
                  {notification.type === 'system_error' && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 text-xs text-gray-300 hover:text-white hover:bg-gray-600/40 flex-1"
                      onClick={(e) => handleCopyError(e, notification)}
                      data-testid={`button-copy-error-${notification.id}`}
                    >
                      <Copy className="w-3 h-3 mr-1" />
                      Copy error
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 text-xs text-green-400 hover:text-green-300 hover:bg-green-500/10 flex-1"
                    onClick={(e) => handleMarkAsRead(e, notification.id)}
                    disabled={markReadMutation.isPending}
                    data-testid={`button-mark-read-${notification.id}`}
                  >
                    <CheckCircle className="w-3 h-3 mr-1" />
                    Mark as Read
                  </Button>
                </div>
              </DropdownMenuItem>
            ))}
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
